import 'core-js/stable'
import 'regenerator-runtime/runtime'

import React, { useEffect } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import { GoogleTagManager } from '@next/third-parties/google'
import { Provider } from 'react-redux'
import { useRouter } from 'next/router'
import { Cookies, CookiesProvider, useCookies } from 'react-cookie'
import type { AppContext, AppProps } from 'next/app'
import { cloneDeep } from 'lodash'
import { DeepPartial } from 'redux'
import NextNProgress from 'nextjs-progressbar'

import environment from 'environment'

import 'semantic-ui-css/semantic.min.css' // Importing this firist such that anything is has can be overwritten by our CSS
import 'mana-font/css/mana.min.css'
import 'assets/scss/global.scss'
import 'assets/scss/fonts.scss'

import variables from 'assets/scss/variables.module.scss'

import { makeStore } from 'redux/store'
import { ClientFlags } from 'redux/active/actions/types'
import { RootState } from 'redux/reducers'
import generateActiveState, { DEFAULT_CLIENT_FLAGS } from 'redux/active/initialState'

import StyledComponentThemeWrapper from 'components/misc/StyledComponentThemeWrapper'
import ErrorBoundary from 'components/misc/ErrorBoundary'
import Downtime from 'pages/downtime'
import { getActiveStream } from 'components/advertisments/StreamAd'

import RequestService from 'services/request.service'

import { createAdditionalImmutableCommands } from 'utils/ImmutableCommands'
import { validateCountryCaptcha, validateServerAuthData, validateTopDektAuth } from 'utils/token.utils'
import { useAdFree } from 'hooks/adFree'

createAdditionalImmutableCommands()

type Props = AppProps & {
  cookies: string
  clientFlags: ClientFlags
  userLocation: string | null
}

const App = ({ Component, cookies, clientFlags, userLocation, ...props }: Props) => {
  const isBrowser = typeof window !== 'undefined'
  const cookieObject = isBrowser ? undefined : new Cookies(cookies)

  const pagePropsRedux: DeepPartial<RootState> = props.pageProps?.redux || {}
  const defaultActiveState = generateActiveState(cookieObject, userLocation)

  pagePropsRedux.active = pagePropsRedux.active
    ? {
        ...pagePropsRedux.active,
        ...defaultActiveState,
        clientFlags,
        userLocation,
      }
    : { ...defaultActiveState, clientFlags, userLocation }

  const store = makeStore(pagePropsRedux)

  const activeStream = getActiveStream(clientFlags)

  const router = useRouter()

  const adFree = useAdFree()
  const [{ tbUniqueId }] = useCookies(['tbUniqueId'])

  useEffect(() => {
    // Used for HMR
    if ((module as Record<string, any>).hot) (module as Record<string, any>).hot.accept()

    // Fix edge scrollbar gutter
    if (!environment.getIsEdge()) document.documentElement.classList.add('corrected-scrollbar-gutter')
  }, [])

  useEffect(() => {
    document.body.classList.toggle('ad-free', adFree)

    try {
      // MV isn't available immediately once we're in the browser. We need to wait a bit.
      // I wish there was a better way of doing this.
      setTimeout(() => {
        // @ts-ignore
        if (!adFree && tbUniqueId && !!window.$adManagementConfig?.web?.identityOptIn) {
          // @ts-ignore
          window.$adManagementConfig.web.identityOptIn({ email: tbUniqueId })
        }
      }, 2500)
    } catch {
      // Do nothing
    }
  }, [adFree])

  const actualPageProps = { ...props.pageProps }

  delete actualPageProps.redux

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
        />

        <link rel="manifest" href="/manifest.json" crossOrigin="use-credentials" />
        <link rel="apple-touch-icon" href="/apple-icon.png"></link>

        <meta name="theme-color" content={variables.archidektOrange} />

        <meta name="application-name" content="Archidekt" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="Archiekt" />

        <meta name="format-detection" content="telephone=no" />

        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="msapplication-config" content="/icons/browserconfig.xml" />
        <meta name="msapplication-TileColor" content={variables.archidektOrange} />
        <meta name="msapplication-tap-highlight" content="no" />

        {environment.runningOnPrivateServer && <meta name="robots" content="noindex" />}

        {/* Bing search console controls */}
        <meta name="msvalidate.01" content="5F7A8FD6F59AD8C6859BB295E9475EA9" />

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" />

        {/* THE FONT IS ACTUALLY IMPORTED IN _document.tsx AS PER NEXT'S DOCS */}

        {/* Invokes service worker */}
        <Script async type="module" src="app.js" />
      </Head>
      <CookiesProvider cookies={isBrowser ? undefined : new Cookies(cookies)}>
        <Provider store={store}>
          <StyledComponentThemeWrapper>
            <NextNProgress options={{ showSpinner: false }} color={variables.archidektOrange} />
            <ErrorBoundary>
              <div
                id="mediavine-settings"
                // data-blocklist-all="1" // Turns off All
                // data-blocklist-leaderboard="1"
                data-blocklist-universal-player-desktop={activeStream ? '1' : '0'}
                // data-blocklist-universal-player-mobile={activeStream ? '1' : '0'}
                data-blocklist-universal-player-mobile="0"
                data-blocklist-sidebar-atf="1"
                data-blocklist-sidebar-btf="1"
                data-blocklist-content-desktop="1"
                data-blocklist-content-mobile="1"
                data-blocklist-adhesion-mobile="1"
                data-blocklist-recipe="1"
                data-blocklist-auto-insert-sticky="1"
                data-blocklist-in-image="1"
                data-blocklist-chicory="1"
                data-blocklist-zergnet="1"
                data-blocklist-interstitial-mobile="1"
                data-blocklist-interstitial-desktop="1"
                data-blocklist-gumgum-skins="1"
                data-expires-at="2028-01-01"></div>

              {environment.isDowntime || clientFlags.rules?.maintenencePageEnabled ? (
                <Downtime />
              ) : (
                <Component key={router.asPath} {...actualPageProps} />
              )}

              <GoogleTagManager gtmId={environment.googleTagManager} />
            </ErrorBoundary>
          </StyledComponentThemeWrapper>
        </Provider>
      </CookiesProvider>
    </>
  )
}

App.getInitialProps = async (context: AppContext): Promise<any> => {
  const validatedAuthData = await validateServerAuthData(context)
  const forcedRedirectUrl = validateTopDektAuth(context)

  if (!validatedAuthData) {
    context.ctx?.res?.writeHead(302, { Location: context.router.pathname })
    context.ctx?.res?.end()

    return { cookies: context.ctx.req?.headers?.cookie }
  }

  if (forcedRedirectUrl) {
    context.ctx?.res?.writeHead(302, { Location: forcedRedirectUrl })
    context.ctx?.res?.end()

    return { cookies: context.ctx.req?.headers?.cookie }
  }

  const rulesUrl = `https://storage.googleapis.com/${environment.environmentName}-client-bucket/client_flags/flags.json`
  const clientFlags = await RequestService.get(rulesUrl).catch(() => cloneDeep(DEFAULT_CLIENT_FLAGS))

  const userIp = (context.ctx?.req?.headers['x-forwarded-for'] || []).toString().split(',')[0] || ''
  const userLocationLookupUrl = userIp ? `https://api.ipinfo.io/lite/${userIp}?token=${environment.ipInfoToken}` : null

  let userLocation = null

  if (userLocationLookupUrl && environment.ipInfoToken) {
    try {
      const locationRes = await RequestService.get(userLocationLookupUrl)

      userLocation = locationRes?.country_code || null
    } catch {
      // pass - no need to do anything, we'll just use the normal fallbacks for user location
    }
  }

  if (context.ctx?.query?.forcestream)
    clientFlags.twitch[context.ctx.query.forcestream as keyof typeof clientFlags.twitch] = true

  const forceCaptchaVerification = validateCountryCaptcha(userLocation, context)

  if (forceCaptchaVerification) {
    context.ctx?.res?.writeHead(302, { Location: '/captcha' })
    context.ctx?.res?.end()
  }

  return { cookies: context.ctx.req?.headers?.cookie, clientFlags, userLocation, userIp }
}

export default App
